import React, {useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PropTypes from 'prop-types';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import SectionHeading from './SectionHeading';
import DescriptionSettings from './DescriptionSettings';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../constants/ProfileSectionsKeys';
import useNotification from '../../../../utility/hooks/useNotification';
import {
    useAddMedicineLookupMutation,
    useAddStarterMedicinesMutation,
    useDeleteMedicineLookupMutation,
    useGetDosageUnitsQuery,
    useGetMedicineLookupsQuery,
    useUpdateMedicineLookupMutation
} from '../../../../api/services/medicinesService';
import {matchesLookupSearch, MEDICINE_NAME_MAX, unitName} from '../../../../utility/medicines';

const emptyMedicine = {id: null, description: '', dosageUnit: '', packAmount: '', brands: []};

// The school's medicine list on the Lookups page. Each medicine has its name, its usual dosage
// unit and pack amount, and its brands, one per strength, each with their own unit and pack
// amount. The Medicine at School form fills these in when a parent selects the medicine.
const MedicineListSettings = ({sectionDescription, onSectionDescriptionChange}) => {
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const {data: medicines = [], isLoading, refetch} = useGetMedicineLookupsQuery();
    const {data: units = []} = useGetDosageUnitsQuery();
    const [addMedicine] = useAddMedicineLookupMutation();
    const [updateMedicine] = useUpdateMedicineLookupMutation();
    const [deleteMedicine] = useDeleteMedicineLookupMutation();
    const [addStarterMedicines, {isLoading: isAddingStarter}] = useAddStarterMedicinesMutation();

    const [editing, setEditing] = useState(null);
    const [offerStarter, setOfferStarter] = useState(false);
    const [search, setSearch] = useState('');
    const [ticking, setTicking] = useState({});
    const shown = medicines.filter(medicine => matchesLookupSearch(medicine, search));

    // The API returns a refusal as validationErrors, for example a name already on the list.
    const failureMessage = (error) => error?.data?.validationErrors?.[0]?.errorMessage ??
        error?.data?.title ??
        'That could not be saved';

    const toNumber = value => (value === '' || value == null ? null : Number(value));

    const handleSave = async () => {
        const body = {
            description: editing.description,
            dosageUnit: editing.dosageUnit || null,
            packAmount: toNumber(editing.packAmount),
            brands: editing.brands
                .filter(brand => brand.name.trim())
                .map(brand => ({
                    name: brand.name,
                    // The name before this edit, so every child holding a renamed brand is renamed too.
                    previousName: brand.previousName ?? null,
                    dosageUnit: brand.dosageUnit || null,
                    packAmount: toNumber(brand.packAmount)
                }))
        };

        try {
            if (editing.id) {
                await updateMedicine({id: editing.id, ...body}).unwrap();
                showSuccessNotification(`${editing.description} saved`);
            } else {
                await addMedicine(body).unwrap();
                showSuccessNotification(`${editing.description} added to the list`);
            }
            await refetch();
            setEditing(null);
        } catch (error) {
            showErrorNotification(failureMessage(error));
        }
    };

    // Unticked, a medicine stays on the list, but the parent's form and the Medical tab no longer offer it.
    const handleActive = async (medicine, isActive) => {
        setTicking(current => ({...current, [medicine.id]: isActive}));
        try {
            await updateMedicine({
                id: medicine.id,
                description: medicine.description,
                dosageUnit: medicine.dosageUnit ?? null,
                packAmount: medicine.packAmount ?? null,
                brands: medicine.brands ?? [],
                isActive
            }).unwrap();
            await refetch();
            showSuccessNotification(isActive
                ? `${medicine.description} is active`
                : `${medicine.description} is inactive. It stays on the list, and forms and profiles no longer offer it`);
        } catch (error) {
            showErrorNotification(failureMessage(error));
        } finally {
            setTicking(({[medicine.id]: _done, ...rest}) => rest);
        }
    };

    const handleDelete = async (medicine) => {
        try {
            await deleteMedicine(medicine.id).unwrap();
            await refetch();
            showSuccessNotification(`${medicine.description} removed from the list`);
        } catch (error) {
            showErrorNotification(failureMessage(error));
        }
    };

    // The standard list joins the school's own: nothing already on the list is changed or repeated.
    const handleAddStarter = async () => {
        try {
            const {medicinesAdded, brandsAdded} = await addStarterMedicines().unwrap();
            await refetch();
            setOfferStarter(false);
            showSuccessNotification(medicinesAdded === 0 && brandsAdded === 0
                ? 'Every medicine and brand on the standard list is already on this list'
                : `Added ${medicinesAdded} ${medicinesAdded === 1 ? 'medicine' : 'medicines'} and ${brandsAdded} ${brandsAdded === 1 ? 'brand' : 'brands'}`);
        } catch (error) {
            showErrorNotification(failureMessage(error));
        }
    };

    const setBrand = (index, changes) => setEditing({
        ...editing,
        brands: editing.brands.map((brand, i) => (i === index ? {...brand, ...changes} : brand))
    });

    const unitSelect = (value, onChange, testId) => (
        <TextField
            select
            fullWidth
            size="small"
            label="Dosage unit"
            value={value ?? ''}
            inputProps={{'data-testid': testId}}
            onChange={(event) => onChange(event.target.value)}
        >
            <MenuItem value="">None</MenuItem>
            {units.map(unit => <MenuItem key={unit.key} value={unit.key}>{unitName(unit.key, units)}</MenuItem>)}
        </TextField>
    );

    if (isLoading) {
        return '';
    }

    return (
        <Box sx={{width: '100%'}} data-testid="medicine-list-settings">
            <SectionHeading title="Medicine at School" testId="medicine-at-school-heading"/>
            <DescriptionSettings
                description={sectionDescription}
                defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.medicineAtSchool]}
                onDescriptionChange={onSectionDescriptionChange}
            />
            <Box marginTop={2} marginBottom={2}>
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: {xs: '100%', sm: 'auto'},
                        '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}
                    }}
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon/>}
                    data-testid="add-medicine-to-list"
                    onClick={() => setEditing({...emptyMedicine, brands: []})}
                >
                    Add new
                </Button>
                <Button
                    sx={{ml: {xs: 0, sm: 2}, mt: {xs: 1, sm: 0}, width: {xs: '100%', sm: 'auto'}}}
                    variant="outlined"
                    data-testid="add-standard-medicines"
                    onClick={() => setOfferStarter(true)}
                >
                    Add the standard list
                </Button>
            </Box>
            <AdmicityDialog
                handleClose={() => setOfferStarter(false)}
                title="Add the standard list"
                open={offerStarter}
                actions={[
                    {label: 'Cancel', onClick: () => setOfferStarter(false)},
                    {label: 'Add', onClick: handleAddStarter, disabled: isAddingStarter}
                ]}
            >
                <Typography variant="body2" sx={{mb: 1}}>
                    The standard list holds the medicines given most often in schools in England and
                    Wales, each with its UK brands, one per strength.
                </Typography>
                <Typography variant="body2">
                    A medicine already on this list keeps its details and gains any brands it does not
                    have. Any medicine or brand can be edited or removed afterwards.
                </Typography>
            </AdmicityDialog>

            <TextField
                fullWidth
                size="small"
                label="Search medicines and brands"
                value={search}
                inputProps={{'data-testid': 'medicine-list-search'}}
                onChange={(event) => setSearch(event.target.value)}
            />

            <Box sx={{maxHeight: 520, overflowY: 'auto', mt: 1}} data-testid="medicine-list-scroll">
            <List sx={{width: '100%'}}>
                {medicines.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No medicines are on the list.
                    </Typography>
                )}
                {medicines.length > 0 && shown.length === 0 && (
                    <Typography variant="body2" color="text.secondary" data-testid="medicine-list-no-match">
                        No medicine or brand on the list matches {search.trim()}.
                    </Typography>
                )}
                {shown.map((medicine, index) => (
                    <ListItem
                        key={medicine.id}
                        data-testid={`medicine-lookup-${medicine.id}`}
                        sx={{backgroundColor: index % 2 === 0 ? '#F0FBFA' : '#FFFFFF', pr: 22, opacity: medicine.isActive === false ? 0.6 : 1}}
                        secondaryAction={
                            <Stack direction="row" alignItems="center">
                                <FormControlLabel
                                    label="Active"
                                    control={(
                                        <Checkbox
                                            size="small"
                                            checked={ticking[medicine.id] ?? medicine.isActive !== false}
                                            disabled={medicine.id in ticking}
                                            inputProps={{'data-testid': `medicine-lookup-${medicine.id}-active`}}
                                            onChange={(event) => handleActive(medicine, event.target.checked)}
                                        />
                                    )}
                                />
                                <Tooltip title="Edit">
                                    <IconButton
                                        data-testid={`medicine-lookup-${medicine.id}-edit`}
                                        onClick={() => setEditing({
                                            id: medicine.id,
                                            description: medicine.description,
                                            dosageUnit: medicine.dosageUnit ?? '',
                                            packAmount: medicine.packAmount ?? '',
                                            brands: (medicine.brands ?? []).map(brand => ({
                                                name: brand.name,
                                                previousName: brand.name,
                                                dosageUnit: brand.dosageUnit ?? '',
                                                packAmount: brand.packAmount ?? ''
                                            }))
                                        })}
                                    >
                                        <EditIcon/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title={medicine.studentsHolding > 0
                                        ? "Remove it from each child's Medicine at School first"
                                        : 'Remove from the list'}
                                >
                                    <span>
                                        <IconButton
                                            edge="end"
                                            data-testid={`medicine-lookup-${medicine.id}-delete`}
                                            onClick={() => handleDelete(medicine)}
                                        >
                                            <DeleteIcon/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        }
                    >
                        <Stack gap={0.5}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography>{medicine.description}</Typography>
                                {medicine.studentsHolding > 0 && (
                                    <Chip
                                        size="small"
                                        label={medicine.studentsHolding === 1
                                            ? 'held by 1 child'
                                            : `held by ${medicine.studentsHolding} children`}
                                    />
                                )}
                            </Stack>
                            {(medicine.brands ?? []).length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {medicine.brands.map(brand => brand.name).join(', ')}
                                </Typography>
                            )}
                        </Stack>
                    </ListItem>
                ))}
            </List>
            </Box>

            <AdmicityDialog
                open={Boolean(editing)}
                title={editing?.id ? 'Edit medicine' : 'New medicine'}
                maxWidth="md"
                handleClose={() => setEditing(null)}
                actions={[
                    {label: 'Cancel', onClick: () => setEditing(null)},
                    {label: 'Save', onClick: handleSave, disabled: !editing?.description?.trim()}
                ]}
            >
                {editing && (
                    <Stack gap={2} sx={{mt: 1}}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Medicine name"
                            data-testid="medicine-description"
                            inputProps={{maxLength: MEDICINE_NAME_MAX}}
                            value={editing.description}
                            onChange={(event) => setEditing({...editing, description: event.target.value})}
                        />
                        <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                            {unitSelect(editing.dosageUnit, value => setEditing({...editing, dosageUnit: value}), 'medicine-unit')}
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Usual amount in a box"
                                value={editing.packAmount}
                                inputProps={{min: 1, 'data-testid': 'medicine-pack'}}
                                onChange={(event) => setEditing({...editing, packAmount: event.target.value})}
                            />
                        </Stack>

                        <Typography variant="subtitle2">Brands, one for each strength</Typography>
                        {editing.brands.map((brand, index) => (
                            <Stack
                                key={index}
                                direction={{xs: 'column', sm: 'row'}}
                                gap={1}
                                alignItems={{sm: 'center'}}
                                data-testid={`medicine-brand-${index}`}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Brand name and strength"
                                    value={brand.name}
                                    inputProps={{maxLength: MEDICINE_NAME_MAX, 'data-testid': `medicine-brand-name-${index}`}}
                                    onChange={(event) => setBrand(index, {name: event.target.value})}
                                />
                                {unitSelect(brand.dosageUnit, value => setBrand(index, {dosageUnit: value}), `medicine-brand-unit-${index}`)}
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Usual amount in a box"
                                    value={brand.packAmount}
                                    inputProps={{min: 1, 'data-testid': `medicine-brand-pack-${index}`}}
                                    onChange={(event) => setBrand(index, {packAmount: event.target.value})}
                                />
                                <IconButton
                                    aria-label="Remove brand"
                                    data-testid={`medicine-brand-remove-${index}`}
                                    onClick={() => setEditing({
                                        ...editing,
                                        brands: editing.brands.filter((_, i) => i !== index)
                                    })}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                            </Stack>
                        ))}
                        <Box>
                            <Button
                                size="small"
                                startIcon={<AddCircleOutlineIcon/>}
                                data-testid="medicine-add-brand"
                                onClick={() => setEditing({
                                    ...editing,
                                    brands: [...editing.brands, {name: '', dosageUnit: editing.dosageUnit, packAmount: ''}]
                                })}
                            >
                                Add a brand
                            </Button>
                        </Box>
                    </Stack>
                )}
            </AdmicityDialog>
        </Box>
    );
};

MedicineListSettings.propTypes = {
    sectionDescription: PropTypes.string,
    onSectionDescriptionChange: PropTypes.func
};

export default MedicineListSettings;
